import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  getAuth,
  verifyPasswordResetCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { logger } from '../utils/logger';

const MIN_PASSWORD_LENGTH = 8;

/**
 * Landing page for the email CTA of passwordless onboarding accounts:
 * verifies the one-time oobCode, lets the user choose a password, then
 * signs them in and drops them into the app.
 */
function CreatePassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oobCode = searchParams.get('oobCode');

  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState('');
  const [linkError, setLinkError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      setLinkError('This link is missing its security code.');
      setVerifying(false);
      return;
    }
    verifyPasswordResetCode(getAuth(), oobCode)
      .then((verifiedEmail) => setEmail(verifiedEmail))
      .catch((error) => {
        logger.error('Password setup link invalid:', error);
        setLinkError('This link has expired or was already used.');
      })
      .finally(() => setVerifying(false));
  }, [oobCode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    if (password.length < MIN_PASSWORD_LENGTH) {
      setFormError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setFormError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode, password);
      try {
        await signInWithEmailAndPassword(auth, email, password);
        navigate('/');
      } catch (signInError) {
        logger.error('Auto sign-in after password setup failed:', signInError);
        navigate('/login');
      }
    } catch (error) {
      logger.error('Password setup failed:', error);
      setFormError('Could not set your password. The link may have expired — request a new one from the login page.');
      setSubmitting(false);
    }
  };

  if (verifying) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (linkError) {
    return (
      <Flex justify="center" align="center" minH="100vh" bg="gray.50" px={4}>
        <Box bg="white" borderRadius="2xl" boxShadow="lg" maxW="md" w="full" p={10} textAlign="center" data-testid="create-password-error">
          <Heading size="md" mb={3}>
            Link expired
          </Heading>
          <Text color="gray.600" mb={6}>
            {linkError} You can request a fresh one with &ldquo;Forgot
            password&rdquo; on the login page.
          </Text>
          <Button as={RouterLink} to="/login" colorScheme="blue">
            Go to login
          </Button>
        </Box>
      </Flex>
    );
  }

  return (
    <Flex justify="center" align="center" minH="100vh" bg="gray.50" px={4}>
      <Box bg="white" borderRadius="2xl" boxShadow="lg" maxW="md" w="full" p={10} data-testid="create-password">
        <Heading size="lg" mb={2} textAlign="center">
          Create your password
        </Heading>
        <Text color="gray.600" mb={8} textAlign="center">
          Signing in as <b>{email}</b>
        </Text>
        <form onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="create-password-input"
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Confirm password</FormLabel>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="create-password-confirm"
              />
            </FormControl>
            {formError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {formError}
              </Alert>
            )}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              w="full"
              isLoading={submitting}
              data-testid="create-password-submit"
            >
              Set Password &amp; Open Dashboard
            </Button>
            <Link as={RouterLink} to="/login" color="gray.500" fontSize="sm">
              Already have a password? Log in
            </Link>
          </VStack>
        </form>
      </Box>
    </Flex>
  );
}

export default CreatePassword;
